/**
 * PAW Record Detail Modal
 *
 * View and inline-edit modal for PAW records.
 * Renders fields directly from the mapped PAWRecord data with inline editing capability.
 */

import React, { useState } from 'react';
import { X, FileText, Plus, Pencil, Save, Link as LinkIcon, Trash2 } from 'lucide-react';
import { PAWRecord } from '../../types';
import { pawService } from '../../services/pawService';
import { StandardButton, StandardTextarea } from '../standard';
import { useUpdatePAWRecord } from '../../services/queries/cyberSweepQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { AUDIT_TEMPLATES } from '../../data/auditTemplates';

interface PAWRecordDetailProps {
  record: PAWRecord;
  onClose: () => void;
  csAuditDisplayName?: string; // e.g., "CyberAudit-0001" instead of SF ID
  onSave?: () => void; // Called after saving a field to trigger parent refetch
}

/**
 * Format date string for display
 */
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return dateStr;
  }
}

/**
 * Format PAW ID with prefix (e.g., "1400233" -> "PAW-1400233")
 */
function formatPAWId(id: string): string {
  if (!id) return '—';
  // If already has PAW- prefix, return as-is
  if (id.toUpperCase().startsWith('PAW-')) return id;
  // Extract numeric portion if present and format
  const numericMatch = id.match(/(\d+)/);
  if (numericMatch) {
    return `PAW-${numericMatch[1]}`;
  }
  return id;
}

// Field options
const FINDINGS_OPTIONS = [
  { value: 'Info not found', label: 'Info not found' },
  { value: 'Listing/s found and deletion has been requested', label: 'Listing/s found and deletion has been requested' },
  { value: 'Site not working', label: 'Site not working' },
  { value: 'Site Deleted Info', label: 'Site Deleted Info' },
  { value: 'Site Not Responding', label: 'Site Not Responding' },
  { value: 'Site Not Honoring', label: 'Site Not Honoring' },
  { value: 'Incomplete', label: 'Incomplete' },
  { value: 'Database non-searchable but deletion requested', label: 'Database non-searchable but deletion requested' },
  { value: 'Listing/s found', label: 'Listing/s found' },
];

const METHOD_OPTIONS = [
  { value: 'Site Opt-out form', label: 'Site Opt-out form' },
  { value: 'Site Contact Us page', label: 'Site Contact Us page' },
  { value: 'Email', label: 'Email' },
  { value: 'Phone call', label: 'Phone call' },
  { value: 'Fax', label: 'Fax' },
];

const PII_OPTIONS = [
  'Full Name',
  'Address',
  'Date of Birth',
  'Phone Number',
  'Email',
  'Business Address',
  'Business Name',
  'Mugshot',
  'ID number',
  'VIN',
  'Debit and Credit Card Numbers',
];

const AUDIT_PHASE_OPTIONS = [
  { value: 'Phase 1', label: 'Phase 1' },
  { value: 'Phase 2', label: 'Phase 2' },
  { value: 'Phase 3', label: 'Phase 3' },
];

type EditableField = 'auditPhase' | 'auditTemplate' | 'findings' | 'method' | 'piiFound' | 'phase2Required' | 'website' | 'status' | 'clientInfoLinks';

// Simple note type for PAW records
interface PAWNote {
  id: string;
  body: string;
  createdBy: string;
  createdDate: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

export const PAWRecordDetail: React.FC<PAWRecordDetailProps> = ({ record, onClose, csAuditDisplayName, onSave }) => {
  const [activeTab, setActiveTab] = useState<'related' | 'details'>('details');
  const [recordData, setRecordData] = useState<PAWRecord>(record);
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editValue, setEditValue] = useState<string | boolean | string[]>('');
  const [newNote, setNewNote] = useState('');
  const [newLink, setNewLink] = useState('');
  const [notes, setNotes] = useState<PAWNote[]>([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const updatePAW = useUpdatePAWRecord();
  const { userName, userId } = useCurrentUser();

  // Handle adding a new note
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const note: PAWNote = {
      id: `note-${Date.now()}`,
      body: newNote.trim(),
      createdBy: userName,
      createdDate: new Date().toISOString(),
    };
    setNotes(prev => [note, ...prev]);
    setNewNote('');
    setIsAddingNote(false);
    // TODO: Persist note to backend when notes API is available
    console.log('[PAW Notes] Added note:', note);
  };

  // Handle editing a note
  const handleStartEditNote = (note: PAWNote) => {
    setEditingNoteId(note.id);
    setEditNoteValue(note.body);
  };

  const handleSaveEditedNote = () => {
    if (!editingNoteId || !editNoteValue.trim()) return;
    setNotes(prev => prev.map(note =>
      note.id === editingNoteId
        ? {
            ...note,
            body: editNoteValue.trim(),
            lastModifiedBy: userName,
            lastModifiedDate: new Date().toISOString(),
          }
        : note
    ));
    setEditingNoteId(null);
    setEditNoteValue('');
    // TODO: Persist note update to backend when notes API is available
    console.log('[PAW Notes] Updated note:', editingNoteId);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
  };

  // Debug: Log record to see available fields in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[PAW Detail] Record data:', recordData);
  }

  // Parse piiFound - it may be stored as semicolon-separated string, array, or boolean (legacy)
  const getPiiFoundArray = (): string[] => {
    const pii = recordData.piiFound as unknown;
    if (Array.isArray(pii)) return pii as string[];
    if (typeof pii === 'string' && pii) return pii.split(';').map((s: string) => s.trim()).filter(Boolean);
    // Legacy boolean support - if true, return empty array (user should select specific PII types)
    return [];
  };

  const handleSaveField = (fieldName: EditableField) => {
    let valueToSave = editValue;
    let gobodoValue = editValue;

    // For piiFound:
    // - Local state: semicolon-separated string for display
    // - Gobodo API: array for multipicklist field
    if (fieldName === 'piiFound' && Array.isArray(editValue)) {
      valueToSave = (editValue as string[]).join(';'); // For local state
      gobodoValue = editValue; // Keep as array for Gobodo multipicklist
    }

    const updates: Partial<PAWRecord> = { [fieldName]: valueToSave };

    if (USE_GOBODO_API) {
      const gobodoUpdates: Record<string, unknown> = {};
      const keyMap: Record<string, string> = {
        website: 'Website__c',
        auditPhase: 'Audit_Phase__c',
        auditTemplate: 'Audit_Template__c',
        findings: 'Findings__c',
        method: 'Method__c',
        piiFound: 'PII_Found__c',
        status: 'Status__c',
        phase2Required: 'Phase_2_Required__c',
        clientInfoLinks: 'Client_Info_Links__c',
      };
      Object.entries(updates).forEach(([k, v]) => {
        // For piiFound, use the array version for Gobodo
        const value = k === 'piiFound' ? gobodoValue : v;
        gobodoUpdates[keyMap[k] ?? k] = value;
      });
      // Always include LastModifiedBy and LastModifiedDate when saving
      // Save both ID and NAME using SF-style field names that Gobodo will store
      gobodoUpdates['LastModifiedById__c'] = userId;
      gobodoUpdates['LastModifiedBy__c'] = userName;      // SF-style name field
      gobodoUpdates['lastModifiedBy'] = userName;         // Also save camelCase for local state
      gobodoUpdates['LastModifiedDate__c'] = new Date().toISOString();
      gobodoUpdates['lastModifiedDate'] = new Date().toISOString();
      console.log('[PAWRecordDetail] Saving field:', fieldName, 'with value:', valueToSave);
      console.log('[PAWRecordDetail] Gobodo updates:', gobodoUpdates);
      console.log('[PAWRecordDetail] Record ID:', recordData.id);
      updatePAW.mutate(
        { id: recordData.id, updates: gobodoUpdates },
        {
          onSuccess: () => {
            console.log('[PAWRecordDetail] Save successful for field:', fieldName);
            // Notify parent to refetch PAW records
            onSave?.();
          },
          onError: (error) => {
            console.error('[PAWRecordDetail] Save FAILED for field:', fieldName, error);
          },
        }
      );
    } else {
      pawService.updatePAWRecord(recordData.id, updates);
      // Notify parent for local storage updates too
      onSave?.();
    }

    setRecordData(prev => ({
      ...prev,
      ...updates,
      lastModifiedBy: userName,
      lastModifiedDate: new Date().toISOString()
    }));
    setEditingField(null);
    setEditValue('');
  };

  const startEditing = (field: EditableField, currentValue: string | boolean | string[] | undefined) => {
    setEditingField(field);
    // For piiFound multiselect, initialize with array
    if (field === 'piiFound') {
      setEditValue(getPiiFoundArray());
    } else {
      setEditValue(currentValue ?? '');
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const handleAddLink = () => {
    if (newLink.trim()) {
      // Convert to array if string
      const currentLinks = Array.isArray(recordData.clientInfoLinks)
        ? recordData.clientInfoLinks
        : (recordData.clientInfoLinks ? recordData.clientInfoLinks.split('\n').filter(Boolean) : []);
      const newLinks = [...currentLinks, newLink.trim()];
      setEditValue(newLinks);
      setEditingField('clientInfoLinks');

      // Save immediately
      const updates: Partial<PAWRecord> = { clientInfoLinks: newLinks };
      if (USE_GOBODO_API) {
        updatePAW.mutate(
          { id: recordData.id, updates: { Client_Info_Links__c: newLinks } },
          {
            onSuccess: () => onSave?.(),
            onError: (error) => console.error('[PAWRecordDetail] Failed to add link:', error),
          }
        );
      } else {
        pawService.updatePAWRecord(recordData.id, updates);
        onSave?.();
      }

      setRecordData(prev => ({
        ...prev,
        clientInfoLinks: newLinks,
        lastModifiedBy: userName,
        lastModifiedDate: new Date().toISOString()
      }));
      setNewLink('');
      setEditingField(null);
    }
  };

  const handleRemoveLink = (index: number) => {
    // Convert to array if string
    const currentLinks = Array.isArray(recordData.clientInfoLinks)
      ? recordData.clientInfoLinks
      : (recordData.clientInfoLinks ? recordData.clientInfoLinks.split('\n').filter(Boolean) : []);
    const newLinks = currentLinks.filter((_: string, i: number) => i !== index);

    const updates: Partial<PAWRecord> = { clientInfoLinks: newLinks };
    if (USE_GOBODO_API) {
      updatePAW.mutate(
        { id: recordData.id, updates: { Client_Info_Links__c: newLinks } },
        {
          onSuccess: () => onSave?.(),
          onError: (error) => console.error('[PAWRecordDetail] Failed to remove link:', error),
        }
      );
    } else {
      pawService.updatePAWRecord(recordData.id, updates);
      onSave?.();
    }

    setRecordData(prev => ({
      ...prev,
      clientInfoLinks: newLinks,
      lastModifiedBy: userName,
      lastModifiedDate: new Date().toISOString()
    }));
  };

  const handlePiiToggle = (piiValue: string) => {
    const currentArray = Array.isArray(editValue) ? editValue as string[] : [];
    if (currentArray.includes(piiValue)) {
      setEditValue(currentArray.filter(v => v !== piiValue));
    } else {
      setEditValue([...currentArray, piiValue]);
    }
  };

  // Helper to render field value or dash
  const renderValue = (value: string | undefined | null): string => {
    return value && value.trim() ? value : '—';
  };

  // Helper to render boolean as Yes/No
  const renderBoolean = (value: boolean | undefined): string => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  };

  // Helper to render PII Found values (multiselect display)
  const renderPiiFound = (): string => {
    const piiArray = getPiiFoundArray();
    if (piiArray.length === 0) return '—';
    return piiArray.join(', ');
  };

  // Input styles
  const selectClass = "w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none";

  // Render links array (without dash for empty)
  const renderLinks = (links: string[] | undefined): React.ReactNode => {
    if (!links || links.length === 0) {
      return null; // No dash, just empty
    }
    return (
      <div className="space-y-1">
        {links.map((link, idx) => (
          <div key={idx} className="flex items-center gap-2 group">
            <LinkIcon className="h-3 w-3 text-blue-600 flex-shrink-0" />
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline break-all text-sm"
            >
              {link}
            </a>
            <button
              onClick={() => handleRemoveLink(idx)}
              className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-opacity"
              title="Remove"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatPAWId(recordData.id)}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">{recordData.clientName || recordData.websiteDisplayName || recordData.website}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex px-6">
              <StandardButton
                onClick={() => setActiveTab('related')}
                variant="ghost"
                className={`py-3 px-4 font-medium rounded-none border-b-2 ${
                  activeTab === 'related'
                    ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Related
              </StandardButton>
              <StandardButton
                onClick={() => setActiveTab('details')}
                variant="ghost"
                className={`py-3 px-4 font-medium rounded-none border-b-2 ${
                  activeTab === 'details'
                    ? 'border-purple-600 text-purple-600 dark:text-purple-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                Details
              </StandardButton>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'related' && (
              <div className="space-y-4">
                {/* Notes Section - SF-style Related List */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Notes ({notes.length})</span>
                    </div>
                    <button
                      onClick={() => setIsAddingNote(true)}
                      className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    >
                      New
                    </button>
                  </div>

                  {/* Content */}
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    {/* Add Note Form */}
                    {isAddingNote && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <StandardTextarea
                          placeholder="Enter your note..."
                          rows={3}
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                        />
                        <div className="flex justify-end gap-2 mt-3">
                          <button
                            onClick={() => { setIsAddingNote(false); setNewNote(''); }}
                            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Notes List */}
                    {notes.length > 0 ? (
                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {notes.map((note) => (
                          <div key={note.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                            {editingNoteId === note.id ? (
                              /* Edit Mode */
                              <div>
                                <StandardTextarea
                                  value={editNoteValue}
                                  onChange={(e) => setEditNoteValue(e.target.value)}
                                  rows={3}
                                />
                                <div className="flex justify-end gap-2 mt-2">
                                  <button
                                    onClick={handleCancelEditNote}
                                    className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSaveEditedNote}
                                    disabled={!editNoteValue.trim()}
                                    className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              /* View Mode */
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{note.body}</p>
                                  {/* System Information for this note */}
                                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Created By: </span>
                                        <span className="font-medium text-blue-600 dark:text-blue-400">{note.createdBy}</span>
                                        <span className="text-gray-400 dark:text-gray-500">, {formatDate(note.createdDate)}</span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">Last Modified By: </span>
                                        {note.lastModifiedBy ? (
                                          <>
                                            <span className="font-medium text-blue-600 dark:text-blue-400">{note.lastModifiedBy}</span>
                                            <span className="text-gray-400 dark:text-gray-500">, {formatDate(note.lastModifiedDate)}</span>
                                          </>
                                        ) : (
                                          <span className="text-gray-400 dark:text-gray-500">—</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleStartEditNote(note)}
                                  className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                                  title="Edit note"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : !isAddingNote && (
                      <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                        No notes to display.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'details' && (
              <div className="space-y-6">
                {/* Fields Section - Single section, no headers */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      {/* Row 1: Performed Audit Website Name | Audit Phase */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Performed Audit Website Name</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {formatPAWId(recordData.id)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Audit Phase</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'auditPhase' ? (
                            <div className="flex items-start gap-2">
                              <select
                                value={editValue as string}
                                onChange={(e) => setEditValue(e.target.value)}
                                className={selectClass}
                                autoFocus
                              >
                                <option value="">Select...</option>
                                {AUDIT_PHASE_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <button onClick={() => handleSaveField('auditPhase')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span>{renderValue(recordData.auditPhase)}</span>
                              <button
                                onClick={() => startEditing('auditPhase', recordData.auditPhase)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>

                      {/* Row 2: CS Audit | Findings */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">CS Audit</dt>
                        <dd className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          {renderValue(csAuditDisplayName || recordData.csAuditId)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Findings</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'findings' ? (
                            <div className="flex items-start gap-2">
                              <select
                                value={editValue as string}
                                onChange={(e) => setEditValue(e.target.value)}
                                className={selectClass}
                                autoFocus
                              >
                                <option value="">Select...</option>
                                {FINDINGS_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <button onClick={() => handleSaveField('findings')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span>{renderValue(recordData.findings)}</span>
                              <button
                                onClick={() => startEditing('findings', recordData.findings)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>

                      {/* Row 3: Audit Template | Method */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Audit Template</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'auditTemplate' ? (
                            <div className="flex items-start gap-2">
                              <select
                                value={editValue as string}
                                onChange={(e) => setEditValue(e.target.value)}
                                className={selectClass}
                                autoFocus
                              >
                                <option value="">Select...</option>
                                {AUDIT_TEMPLATES.map(tpl => (
                                  <option key={tpl.id} value={tpl.name}>{tpl.name}</option>
                                ))}
                              </select>
                              <button onClick={() => handleSaveField('auditTemplate')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">{renderValue(recordData.auditTemplate)}</span>
                              <button
                                onClick={() => startEditing('auditTemplate', recordData.auditTemplate)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Method</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'method' ? (
                            <div className="flex items-start gap-2">
                              <select
                                value={editValue as string}
                                onChange={(e) => setEditValue(e.target.value)}
                                className={selectClass}
                                autoFocus
                              >
                                <option value="">Select...</option>
                                {METHOD_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <button onClick={() => handleSaveField('method')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span>{renderValue(recordData.method)}</span>
                              <button
                                onClick={() => startEditing('method', recordData.method)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>

                      {/* Row 4: Website | PII Found */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Website</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'website' ? (
                            <div className="flex items-start gap-2">
                              <input
                                type="text"
                                value={editValue as string}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-2 py-1 text-sm border border-blue-500 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleSaveField('website')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">{renderValue(recordData.websiteDisplayName || recordData.website)}</span>
                              <button
                                onClick={() => startEditing('website', recordData.website)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">PII Found</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'piiFound' ? (
                            <div className="space-y-2">
                              <div className="max-h-48 overflow-y-auto border border-blue-500 rounded p-2 bg-white dark:bg-gray-700">
                                {PII_OPTIONS.map(pii => (
                                  <label key={pii} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 px-1 rounded">
                                    <input
                                      type="checkbox"
                                      checked={(editValue as string[]).includes(pii)}
                                      onChange={() => handlePiiToggle(pii)}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{pii}</span>
                                  </label>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <StandardButton
                                  onClick={() => handleSaveField('piiFound')}
                                  variant="primary"
                                  size="sm"
                                  leftIcon={<Save className="h-3 w-3" />}
                                >
                                  Save
                                </StandardButton>
                                <StandardButton
                                  onClick={cancelEditing}
                                  variant="secondary"
                                  size="sm"
                                  leftIcon={<X className="h-3 w-3" />}
                                >
                                  Cancel
                                </StandardButton>
                              </div>
                            </div>
                          ) : (
                            <div className="group flex items-start gap-2">
                              <span>{renderPiiFound()}</span>
                              <button
                                onClick={() => startEditing('piiFound', undefined)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>

                      {/* Row 5: Phase 2 Required | Link(s) for client information */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Phase 2 Required</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {editingField === 'phase2Required' ? (
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editValue as boolean}
                                  onChange={(e) => setEditValue(e.target.checked)}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  autoFocus
                                />
                                <span className="text-sm">{(editValue as boolean) ? 'Yes' : 'No'}</span>
                              </label>
                              <button onClick={() => handleSaveField('phase2Required')} className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300" title="Save">
                                <Save className="h-4 w-4" />
                              </button>
                              <button onClick={cancelEditing} className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" title="Cancel">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="group flex items-center gap-2">
                              <span>{renderBoolean(recordData.phase2Required)}</span>
                              <button
                                onClick={() => startEditing('phase2Required', recordData.phase2Required)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity"
                                title="Edit"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Link(s) for client information</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {renderLinks(
                            Array.isArray(recordData.clientInfoLinks)
                              ? recordData.clientInfoLinks
                              : (recordData.clientInfoLinks ? recordData.clientInfoLinks.split('\n').filter(Boolean) : undefined)
                          )}
                          {/* Add link input */}
                          <div className="flex gap-2 mt-2">
                            <input
                              type="text"
                              value={newLink}
                              onChange={(e) => setNewLink(e.target.value)}
                              placeholder="Add link..."
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAddLink();
                                }
                              }}
                            />
                            <button
                              onClick={handleAddLink}
                              disabled={!newLink.trim()}
                              className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </dd>
                      </div>

                      {/* Row 6: Created By | Last Modified By */}
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Created By</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {recordData.createdByName || recordData.createdBy || '—'}
                          {recordData.createdDate && (recordData.createdByName || recordData.createdBy) && (
                            <span className="text-gray-500 dark:text-gray-400 ml-1">
                              , {formatDate(recordData.createdDate)}
                            </span>
                          )}
                          {recordData.createdDate && !recordData.createdByName && !recordData.createdBy && (
                            <span>{formatDate(recordData.createdDate)}</span>
                          )}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Last Modified By</dt>
                        <dd className="text-sm text-gray-900 dark:text-gray-100">
                          {recordData.lastModifiedByName || recordData.lastModifiedBy || '—'}
                          {recordData.lastModifiedDate && (recordData.lastModifiedByName || recordData.lastModifiedBy) && (
                            <span className="text-gray-500 dark:text-gray-400 ml-1">
                              , {formatDate(recordData.lastModifiedDate)}
                            </span>
                          )}
                          {recordData.lastModifiedDate && !recordData.lastModifiedByName && !recordData.lastModifiedBy && (
                            <span>{formatDate(recordData.lastModifiedDate)}</span>
                          )}
                        </dd>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
